import React, { useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/data/translations";

const LocalAttractionsHeroSection = () => {
  const { language } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Safari video autoplay fix - Programmatic play trigger
  useEffect(() => {
    const playVideo = async () => {
      if (videoRef.current) {
        try {
          await videoRef.current.play();
        } catch (error) {
          console.log("Video autoplay prevented:", error);
        }
      }
    };

    // Try to play when component mounts
    playVideo();

    // Also try when video metadata is loaded
    const video = videoRef.current;
    if (video) {
      video.addEventListener("loadedmetadata", playVideo);
      video.addEventListener("canplay", playVideo);
      return () => {
        video.removeEventListener("loadedmetadata", playVideo);
        video.removeEventListener("canplay", playVideo);
      };
    }
  }, []);

  return (
    <div className="relative h-screen overflow-hidden" data-oid=".1z1qcu">
      {/* Video Background */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        data-oid=":vvprsr"
      >
        <source
          src="/videos/experiences-hero-720p-optimized.mp4"
          type="video/mp4"
          data-oid="-lastnu"
        />

        {/* Fallback image if video doesn't load */}
        <img
          src="/assets/summer-alpine-scene.jpg"
          alt="Alpine landscape"
          className="absolute inset-0 w-full h-full object-cover"
          data-oid="f2r.r1n"
        />
      </video>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" data-oid="o7p.f68" />

      {/* Content */}
      <div
        className="relative z-10 flex items-center justify-center h-full text-center text-white"
        data-oid="v4o72mv"
      >
        <div className="max-w-4xl px-6" data-oid="dvo0.9w">
          <h1
            className="text-5xl md:text-6xl mb-6 font-playfair"
            data-oid="hm78o4s"
          >
            {getTranslation(language, "localAttractions.hero.title")}
          </h1>
          <p
            className="text-xl md:text-2xl mb-8 font-lato text-white"
            data-oid="p5yihoi"
          >
            {getTranslation(language, "localAttractions.hero.subtitle")}
          </p>
          <div className="flex justify-center items-center" data-oid="_b9xiy0">
            <Button
              asChild
              size="lg"
              className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              trackingType="booking"
              trackingLabel={getTranslation(
                language,
                "localAttractions.hero.bookStay",
              )}
              trackingLocation="Local Attractions Hero"
              data-oid="s9my2as"
            >
              <a
                href={import.meta.env.VITE_BOOKING_URL}
                data-oid="fl3vrq8"
              >
                {getTranslation(language, "localAttractions.hero.bookStay")}
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce"
        data-oid="qwe_t.7"
      >
        <ChevronDown className="w-8 h-8 text-white/70" data-oid="59xqto1" />
      </div>
    </div>
  );
};

export default LocalAttractionsHeroSection;
